/**
 * SCWT-PM-V2 Phase 3 — PM tab strip.
 *
 * Overview / Properties / Landlords / Book / Tests / Retests /
 * Local Partner / Marketing / Settings.
 *
 * Only visible when `property_manager_v2` flag is ON. When OFF the
 * legacy portfolio-only dashboard is rendered by the parent page.
 */
import { useState } from "react";
import { LayoutDashboard, Building2, Users, CalendarClock, FlaskConical, RefreshCw, ShieldCheck, Megaphone, Settings2, Wrench, Droplet } from "lucide-react";
import { useFeature } from "@/lib/useFeature";

export const PM_TABS = [
  { key: "overview",      label: "Overview",      icon: LayoutDashboard },
  { key: "properties",    label: "Properties",    icon: Building2 },
  { key: "landlords",     label: "Landlords",     icon: Users },
  { key: "book",          label: "Book",          icon: CalendarClock },
  { key: "tests",         label: "Tests",         icon: FlaskConical },
  { key: "retests",       label: "Retests",       icon: RefreshCw },
  { key: "local_partner", label: "Local Partner", icon: ShieldCheck },
  // Phase 6 — gated on `pm_corrective_lead_pipeline`.
  { key: "corrective_leads", label: "Corrective Leads", icon: Wrench, flag: "pm_corrective_lead_pipeline" },
  // Phase 7 — gated on `landlord_water_health_program`.
  { key: "water_health",  label: "Water Health",  icon: Droplet, flag: "landlord_water_health_program" },
  { key: "marketing",     label: "Marketing",     icon: Megaphone },
  { key: "settings",      label: "Settings",      icon: Settings2 },
];

export function PmTabsBar({ active, onChange }) {
  const { isVisible: leadsFlag } = useFeature("pm_corrective_lead_pipeline");
  const { isVisible: waterHealthFlag } = useFeature("landlord_water_health_program");
  const visibleTabs = PM_TABS.filter((t) => {
    if (!t.flag) return true;
    if (t.flag === "pm_corrective_lead_pipeline") return leadsFlag;
    if (t.flag === "landlord_water_health_program") return waterHealthFlag;
    return false;
  });
  return (
    <nav data-testid="pm-tabs-bar"
          className="flex gap-1 overflow-x-auto bg-white rounded-full border border-slate-200 p-1 shadow-sm mb-6">
      {visibleTabs.map((t) => {
        const Icon = t.icon;
        const isActive = t.key === active;
        return (
          <button
            key={t.key}
            data-testid={`pm-tab-${t.key}`}
            onClick={() => onChange(t.key)}
            className={`inline-flex items-center gap-1.5 flex-shrink-0 rounded-full text-xs sm:text-sm font-semibold px-3 sm:px-4 py-2 transition-colors ${
              isActive
                ? "bg-cyan-700 text-white"
                : "text-slate-600 hover:bg-slate-100"
            }`}>
            <Icon className="w-3.5 h-3.5" />
            {t.label}
          </button>
        );
      })}
    </nav>
  );
}

export function usePmTab(defaultKey = "overview") {
  const [tab, setTab] = useState(() => {
    try {
      const url = new URL(window.location.href);
      const q = url.searchParams.get("tab");
      if (q && PM_TABS.some((t) => t.key === q)) return q;
    } catch { /* SSR safety */ }
    return defaultKey;
  });
  const setter = (key) => {
    setTab(key);
    try {
      const url = new URL(window.location.href);
      url.searchParams.set("tab", key);
      window.history.replaceState({}, "", url.toString());
    } catch { /* ignore */ }
  };
  return [tab, setter];
}
